import { inject, Service } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { catchError, EMPTY, from, last, map, Observable, ObservableInput, of, retry, switchMap, throwError, timer } from 'rxjs';

@Service()
export class NetworkService
{
  private http: HttpClient = inject(HttpClient);

  // Headers are added automatically by the HTTP interceptor, so no need to add them here

  constructor(  ) 
  {
      
  }

  public sendRequest(url:string, payload: any, method: 'get' | 'post' | 'put' | 'delete', withCredentials: boolean = false): Observable<any>
  {
    return this.sendRequestRaw(url, payload, method, null, withCredentials);
  }

  public sendRequestRaw(url:string, payload: any, method: string, headers: HttpHeaders | null, withCredentials: boolean = false): Observable<any>
  {
    const options: any = { 'withCredentials': withCredentials };
    if (headers) {
      options['headers'] = headers;
    }

    if ( method == 'get' ) {
      return this.http.get<any>(url, options).pipe(
        retry({ count: 3, delay: this.retryDelay }),
        catchError((error) => { return this.handleError(error); })
      );
    } else if ( method == 'post' ) {
      return this.http.post<any>(url, payload, options).pipe(
        retry({ count: 3, delay: this.retryDelay }),
        catchError((error) => { return this.handleError(error); })
      );
    } else if ( method == 'put' ) {
      return this.http.put<any>(url, payload, options).pipe(
        retry({ count: 3, delay: this.retryDelay }),
        catchError((error) => { return this.handleError(error); })
      );
    } else if ( method == 'delete' ) {
      return this.http.delete<any>(url, options).pipe(
        retry({ count: 3, delay: this.retryDelay }),
        catchError((error) => { return this.handleError(error); })
      );
    } else {
        return EMPTY;
    }
  }

  private retryDelay( error: HttpErrorResponse ): ObservableInput<any>
  {
    if ( error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500 ) {
      return timer(1000);
    } else {
      return throwError(() => error);
    }
  }

  private handleError(error: HttpErrorResponse): Observable<any> 
  {
    let errorMessage = '';

    switch( error.status ) {
      case 0: errorMessage = "Network error occurred. Please check your connection and try again in a few minutes."; break;
      case 401: errorMessage = "You are not authorized to access the required resource or your session has expired."; break;
      case 403: errorMessage = "You are not authorized to access the required resource or your session has expired."; break;
      case 404: errorMessage = "The requested resource was not found."; break;
      case 500: errorMessage = "Internal error encountered processing your request. Please try again. If the problem persists, contact the system administrator."; break;
      default: errorMessage = "Unable to access the requested resource. Please try again in a few minutes."; break;
    }

    return throwError(() => new Error(errorMessage));
  }

  public uploadFile(url:string, file: FileUploadModel, content: any | null): Observable<any>
  {
    let fd = new FormData();

    if( content != null ) {
      fd.append('content', JSON.stringify(content));
    }

    fd.append('file', file.data);
    file.inProgress = true;

    return this.http.post(url, fd,
      {
        reportUploadProgress: true,
        observe: 'events'
      }
    ).pipe(
      map((event: any) => {
        switch (event!.type) {
          case HttpEventType.UploadProgress:
            file.progress = Math.round(event.loaded * 100 / event.total);
            file.uploadedSize = event.loaded;
            break;
          case HttpEventType.Response:
            return event.body;
        }
      }),
      // tap(message => { console.log(message) }),
      last(),
      catchError((error: HttpErrorResponse) => {
        file.inProgress = false;
        file.canRetry = true;
        return of({ success: false, message: `${file.data.name} upload failed.`});
      })
    );
  }

  public downloadFile(url:string): Observable<any>
  {
    return this.http.get(url, {responseType:'blob'});
  }

  public downloadMedia(url: string, payload: any): Observable<any>
  {
    return this.http.post(url, payload, {responseType:'blob'});
  }

  public downloadExternalFile(url:string): Observable<Blob>
  {
    return (this.http.get(url, {responseType: 'blob'}) as Observable<any>).pipe(
      switchMap((blob: Blob) => {
        // Check if it's JSON (error response)
        if (blob.type === 'application/json' || blob.type === 'text/plain') {
          return from(blob.text()).pipe(
            map(text => {
              const json = JSON.parse(text);
              if (!json.success) {
                throw new Error(json.message);
              }
              return blob;
            })
          );
        }

        return of(blob);
      })
    );
  }
}

export class FileUploadModel {
    data!: File;
    state!: string;
    inProgress!: boolean;
    progress!: number;
    uploadedSize!: number
    canRetry!: boolean;
    canCancel!: boolean;
}
